import React, {useState, useEffect, useCallback, useRef} from 'react';
import {DndProvider} from 'react-dnd';
import {HTML5Backend} from 'react-dnd-html5-backend';
import {TouchBackend} from 'react-dnd-touch-backend';
import {
    Trash2,
    Edit,
    Copy,
    Eye,
    EyeOff,
    Key,
    CheckCircle,
    XCircle,
    CalendarCheck,
    CalendarX,
    Users
} from 'lucide-react';
import {useQuery, useMutation, useQueryClient} from '@tanstack/react-query';
import DraggableEmployeeRow from './Employee/DraggableEmployeeRow';
import {EmployeeAvatar} from './Employee/employeeAppearance';
import {api} from "../../config/api";
import secureStorage from "../../utils/secureStorage";
import useAttendanceSettings from "../Attendance/useAttendanceSettings";
import {RowActions, ListEmpty, thClass, mobileRowClass} from "../../stock/StockUI";
import {SettingsToolbar, StatusBadge, AddAction, ListLoader, Modal, ModalFooter} from "./SettingsUI";

// Detect if device is touch-enabled
const isTouchDevice = () => {
    return (('ontouchstart' in window) ||
        (navigator.maxTouchPoints > 0) ||
        (navigator.msMaxTouchPoints > 0));
};

// "Actions ▾" menu of an employee row
const ActionsDropdown = ({employee, onEdit, onDelete, onDuplicate, onToggleActive, onShowPin}) => (
    <RowActions actions={[
        {label: 'Modifier', icon: Edit, onClick: () => onEdit(employee), tone: 'blue'},
        {label: 'Afficher le PIN', icon: Key, onClick: () => onShowPin(employee), tone: 'blue'},
        {label: 'Dupliquer', icon: Copy, onClick: () => onDuplicate(employee), tone: 'green'},
        employee.active
            ? {label: 'Désactiver', icon: EyeOff, onClick: () => onToggleActive(employee), tone: 'blue'}
            : {label: 'Activer', icon: Eye, onClick: () => onToggleActive(employee), tone: 'green'},
        {label: 'Supprimer', icon: Trash2, onClick: () => onDelete(employee.id), tone: 'red', danger: true},
    ]}/>
);

const EmployeesList = ({
                           searchTerm,
                           setSearchTerm,
                           debouncedSearchTerm,
                           selectedItems,
                           setSelectedItems,
                           selectedAll,
                           setSelectedAll,
                           showNotification,
                           onCreateEmployee,
                           onEditEmployee
                       }) => {
    const queryClient = useQueryClient();
    const {enabled: attendanceEnabled} = useAttendanceSettings();
    const [filteredEmployees, setFilteredEmployees] = useState([]);
    const [pendingUpdates, setPendingUpdates] = useState([]);
    // ✅ FIX: Utiliser useRef au lieu de useState pour éviter les re-renders
    const updateTimeoutRef = useRef(null);
    const [showPinModal, setShowPinModal] = useState(false);
    const [selectedEmployeePin, setSelectedEmployeePin] = useState(null);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null);

    // Choose backend based on device type
    const backend = isTouchDevice() ? TouchBackend : HTML5Backend;


    // Fetch employees using React Query
    const fetchEmployees = async () => {
        const accountId = await secureStorage.getItem("selectedAccount");
        const response = await api.get('/employees', {
            headers: {
                'X-account-id': accountId.id,
                'Accept': 'application/json'
            }
        });

        // Extract items from paginated response
        const employeesData = response.data.items || response.data;

        // Sort by order_calander
        const sortedEmployees = employeesData.sort((a, b) =>
            (a.order_calander || 0) - (b.order_calander || 0)
        );

        return sortedEmployees;
    };

    const {data: employees, isLoading, error} = useQuery({
        queryKey: ['employees'],
        queryFn: fetchEmployees,
    });

    // Filter employees based on search
    useEffect(() => {
        if (!employees) {
            setFilteredEmployees([]);
            return;
        }

        if (!debouncedSearchTerm) {
            setFilteredEmployees(employees);
            return;
        }

        const filtered = employees.filter(employee =>
            employee.name?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
            employee.email?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
            employee.profil?.label?.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
        );

        setFilteredEmployees(filtered);
    }, [debouncedSearchTerm, employees]);

    // Batch update employees order
    const updateEmployeeOrdersBatch = useMutation({
        mutationFn: async (updates) => {
            const accountId = await secureStorage.getItem("selectedAccount");
            await api.post('/employees/reorder',
                {orders: updates},
                {
                    headers: {
                        'X-account-id': accountId.id,
                        'Accept': 'application/json',
                        'Content-Type': 'application/json'
                    }
                }
            );
            return updates;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['employees']});
            showNotification('success', 'Ordre mis à jour avec succès');
            setPendingUpdates([]);
        },
        onError: (error) => {
            console.error('Error updating employee orders:', error);
            queryClient.invalidateQueries({queryKey: ['employees']}); // Revert by refetching
            showNotification('error', 'Erreur lors de la mise à jour de l\'ordre');
        }
    });

    // Delete employees mutation
    const deleteEmployeesMutation = useMutation({
        mutationFn: async (employeeIds) => {
            const accountId = await secureStorage.getItem("selectedAccount");

            // Delete employees one by one
            const results = await Promise.all(
                employeeIds.map(id =>
                    api.delete(`/employees/${id}`, {
                        headers: {
                            'X-account-id': accountId.id,
                            'Accept': 'application/json'
                        }
                    }).catch(err => {
                        return {error: err, id};
                    })
                )
            );

            // Check for errors
            const failures = results.filter(r => r && r.error);
            if (failures.length > 0) {
                if (failures.length === employeeIds.length) {
                    throw new Error("Impossible de supprimer les employés sélectionnés");
                } else {
                    throw new Error(`${failures.length} sur ${employeeIds.length} employés n'ont pas pu être supprimés`);
                }
            }

            return employeeIds;
        },
        onMutate: () => {
            showNotification('loading', 'Suppression en cours...');
        },
        onSuccess: () => {
            // Reset selection and refetch data
            setSelectedItems([]);
            setSelectedAll(false);
            queryClient.invalidateQueries({queryKey: ['employees']});
            showNotification('success', 'Employés supprimés avec succès');
            setShowConfirmDialog(false);
        },
        onError: (error) => {
            showNotification('error', `Erreur: ${error.message}`);
            setShowConfirmDialog(false);
        }
    });

    // Duplicate employee mutation
    const duplicateEmployeeMutation = useMutation({
        mutationFn: async (employee) => {
            const accountId = await secureStorage.getItem("selectedAccount");

            const response = await api.post(`/employees/${employee.id}/duplicate`, {}, {
                headers: {
                    'X-account-id': accountId.id,
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                }
            });

            return response.data;
        },
        onMutate: () => {
            showNotification('loading', 'Duplication en cours...');
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['employees']});
            showNotification('success', 'Employé dupliqué avec succès');
        },
        onError: (error) => {
            console.error('Error duplicating employee:', error);
            showNotification('error', 'Erreur lors de la duplication de l\'employé');
        }
    });

    // Toggle active status mutation
    const toggleActiveEmployeeMutation = useMutation({
        mutationFn: async (employee) => {
            const accountId = await secureStorage.getItem("selectedAccount");

            const updatedEmployee = {
                ...employee,
                active: !employee.active,
                profil: employee.profil?.id || employee.profil
            };

            const response = await api.put(`/employees/${employee.id}`, updatedEmployee, {
                headers: {
                    'X-account-id': accountId.id,
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                }
            });

            return response.data;
        },
        onMutate: () => {
            showNotification('loading', 'Mise à jour en cours...');
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({queryKey: ['employees']});
            showNotification('success', data.active ? 'Employé activé avec succès' : 'Employé désactivé avec succès');
            setShowConfirmDialog(false);
        },
        onError: (error) => {
            console.error('Error toggling employee status:', error);
            showNotification('error', 'Erreur lors de la mise à jour du statut');
            setShowConfirmDialog(false);
        }
    });

    // Bulk update a boolean field (active / showOnCalendar) on the selected employees
    const BULK_SUCCESS_MESSAGES = {
        active: {true: 'Employés activés avec succès', false: 'Employés désactivés avec succès'},
        showOnCalendar: {true: 'Employés affichés dans le calendrier', false: 'Employés masqués du calendrier'},
    };

    const bulkUpdateMutation = useMutation({
        mutationFn: async ({ids, field, value}) => {
            const accountId = await secureStorage.getItem("selectedAccount");

            // Only touch employees whose value actually changes (showOnCalendar defaults to true)
            const currentValue = (e) => field === 'showOnCalendar' ? e.showOnCalendar !== false : !!e[field];
            const targets = (employees || []).filter(e => ids.includes(e.id) && currentValue(e) !== value);

            const results = await Promise.all(
                targets.map(employee =>
                    api.put(`/employees/${employee.id}`, {
                        ...employee,
                        [field]: value,
                        // The API's employee processor reads the snake_case key
                        ...(field === 'showOnCalendar' ? {show_on_calendar: value} : {}),
                        profil: employee.profil?.id || employee.profil
                    }, {
                        headers: {
                            'X-account-id': accountId.id,
                            'Accept': 'application/json',
                            'Content-Type': 'application/json'
                        }
                    }).catch(err => ({error: err, id: employee.id}))
                )
            );

            const failures = results.filter(r => r && r.error);
            if (failures.length > 0) {
                throw new Error(`${failures.length} sur ${targets.length} employés n'ont pas pu être mis à jour`);
            }

            return {field, value};
        },
        onMutate: () => {
            showNotification('loading', 'Mise à jour en cours...');
        },
        onSuccess: ({field, value}) => {
            setSelectedItems([]);
            setSelectedAll(false);
            queryClient.invalidateQueries({queryKey: ['employees']});
            showNotification('success', BULK_SUCCESS_MESSAGES[field][value]);
            setShowConfirmDialog(false);
        },
        onError: (error) => {
            queryClient.invalidateQueries({queryKey: ['employees']});
            showNotification('error', `Erreur: ${error.message}`);
            setShowConfirmDialog(false);
        }
    });

    // Fetch employee PIN mutation
    const fetchEmployeePinMutation = useMutation({
        mutationFn: async (employeeId) => {
            const accountId = await secureStorage.getItem("selectedAccount");

            const response = await api.get(`/employees/${employeeId}`, {
                headers: {
                    'X-account-id': accountId.id,
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                }
            });

            return response.data;
        },
        onMutate: () => {
            showNotification('loading', 'Chargement du PIN...');
        },
        onSuccess: (data) => {
            setSelectedEmployeePin(data);
            setShowPinModal(true);
            showNotification('success', 'PIN récupéré avec succès');
        },
        onError: (error) => {
            console.error('Error fetching employee PIN:', error);
            showNotification('error', 'Erreur lors de la récupération du PIN');
        }
    });

    // ✅ FIX: Batch updates with debounce - retirer updateTimeout des dépendances
    const scheduleBatchUpdate = useCallback((updates) => {
        // Clear existing timeout using ref
        if (updateTimeoutRef.current) {
            clearTimeout(updateTimeoutRef.current);
        }

        // Schedule new batch update after 1 second of inactivity
        const timeout = setTimeout(() => {
            updateEmployeeOrdersBatch.mutate(updates);
        }, 1000);

        updateTimeoutRef.current = timeout;
    }, [updateEmployeeOrdersBatch]);

    // Move employee (drag and drop)
    const moveEmployee = useCallback((dragIndex, hoverIndex) => {
        const draggedEmployee = filteredEmployees[dragIndex];

        // Update local state optimistically
        const newEmployees = [...filteredEmployees];
        newEmployees.splice(dragIndex, 1);
        newEmployees.splice(hoverIndex, 0, draggedEmployee);

        // Update order_calander for all affected employees
        const updatedEmployees = newEmployees.map((emp, index) => ({
            ...emp,
            order_calander: index + 1
        }));

        setFilteredEmployees(updatedEmployees);

        // Prepare batch updates
        const updates = [];
        for (let i = Math.min(dragIndex, hoverIndex); i <= Math.max(dragIndex, hoverIndex); i++) {
            updates.push({
                id: updatedEmployees[i].id,
                order: i + 1
            });
        }

        setPendingUpdates(updates);
        scheduleBatchUpdate(updates);
    }, [filteredEmployees, scheduleBatchUpdate]);

    // Handle select all
    const handleSelectAll = () => {
        if (!employees || employees.length === 0) return;

        const newSelectedAll = !selectedAll;
        setSelectedAll(newSelectedAll);
        setSelectedItems(newSelectedAll ? filteredEmployees.map(emp => emp.id) : []);
    };

    // Handle select item
    const handleSelectItem = (id, e) => {
        if (e) e.stopPropagation();
        setSelectedItems(prev =>
            prev.includes(id)
                ? prev.filter(itemId => itemId !== id)
                : [...prev, id]
        );
    };

    // Handler pour supprimer un employé
    const handleDeleteEmployee = (employeeId) => {
        setConfirmAction({
            type: 'delete',
            employeeId: employeeId,
            message: 'Êtes-vous sûr de vouloir supprimer cet employé ?'
        });
        setShowConfirmDialog(true);
    };

    // Handler pour dupliquer un employé
    const handleDuplicateEmployee = (employee) => {
        duplicateEmployeeMutation.mutate(employee);
    };

    // Handler pour activer/désactiver un employé
    const handleToggleActive = (employee) => {
        setConfirmAction({
            type: 'toggleActive',
            employee: employee,
            message: employee.active
                ? 'Êtes-vous sûr de vouloir désactiver cet employé ?'
                : 'Êtes-vous sûr de vouloir activer cet employé ?'
        });
        setShowConfirmDialog(true);
    };

    // Handlers pour les actions groupées sur les employés sélectionnés
    const selectionLabel = () => {
        const count = selectedItems.length;
        return `${count} employé${count > 1 ? 's' : ''}`;
    };

    const handleBulkUpdate = (field, value) => {
        const verb = field === 'active'
            ? (value ? 'activer' : 'désactiver')
            : (value ? 'afficher dans le calendrier' : 'masquer du calendrier');
        setConfirmAction({
            type: 'bulkUpdate',
            field,
            value,
            message: `Êtes-vous sûr de vouloir ${verb} ${selectionLabel()} ?`
        });
        setShowConfirmDialog(true);
    };

    const handleBulkDelete = () => {
        setConfirmAction({
            type: 'bulkDelete',
            message: `Êtes-vous sûr de vouloir supprimer ${selectionLabel()} ? Cette action est irréversible.`
        });
        setShowConfirmDialog(true);
    };

    // Handler pour afficher le PIN
    const handleShowPin = (employee) => {
        // Appeler l'API pour récupérer les détails complets de l'employé avec le PIN
        fetchEmployeePinMutation.mutate(employee.id);
    };

    // Confirmer l'action
    const confirmDialogAction = () => {
        if (!confirmAction) return;

        if (confirmAction.type === 'delete') {
            deleteEmployeesMutation.mutate([confirmAction.employeeId]);
        } else if (confirmAction.type === 'toggleActive') {
            toggleActiveEmployeeMutation.mutate(confirmAction.employee);
        } else if (confirmAction.type === 'bulkUpdate') {
            bulkUpdateMutation.mutate({ids: selectedItems, field: confirmAction.field, value: confirmAction.value});
        } else if (confirmAction.type === 'bulkDelete') {
            deleteEmployeesMutation.mutate(selectedItems);
        }
    };

    // ✅ FIX: Cleanup timeout on unmount - utiliser ref au lieu de state
    useEffect(() => {
        return () => {
            if (updateTimeoutRef.current) {
                clearTimeout(updateTimeoutRef.current);
            }
        };
    }, []);

    const bulkButtonClass = 'inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 shadow-sm transition-all';

    const closePinModal = () => {
        setShowPinModal(false);
        setSelectedEmployeePin(null);
    };

    const closeConfirm = () => {
        setShowConfirmDialog(false);
        setConfirmAction(null);
    };

    const isDanger = ['delete', 'bulkDelete'].includes(confirmAction?.type);

    // Status, booking visibility and badge chips
    const EmployeeBadges = ({employee}) => (
        <span className="inline-flex flex-wrap items-center gap-1.5">
            <StatusBadge active={employee.active}/>
            {employee.showOnCalendar === false && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300" title="Non affiché dans la réservation">
                    Masqué réservation
                </span>
            )}
            {attendanceEnabled && employee.badgeNumber != null && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300" title="N° de badge sur la pointeuse">
                    Badge n° {employee.badgeNumber}
                </span>
            )}
        </span>
    );

    const rowMenu = (employee) => (
        <ActionsDropdown
            employee={employee}
            onEdit={onEditEmployee}
            onDelete={handleDeleteEmployee}
            onDuplicate={handleDuplicateEmployee}
            onToggleActive={handleToggleActive}
            onShowPin={handleShowPin}
        />
    );

    return (
        <DndProvider backend={backend}>
            <div>
                <AddAction onClick={onCreateEmployee}/>
                <SettingsToolbar search={searchTerm} onSearch={setSearchTerm} placeholder="Rechercher par nom, email ou profil..."/>

                {/* ── Selection Bar ── */}
                {selectedItems.length > 0 && (
                    <div className="flex flex-col lg:flex-row lg:items-center gap-3 p-4 bg-primary/10 dark:bg-primary/20 border-b border-primary/20 selection-bar-appear">
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-primary flex items-center justify-center rounded-full shadow flex-shrink-0">
                                <CheckCircle className="w-4 h-4 text-white"/>
                            </div>
                            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 whitespace-nowrap">
                                {selectedItems.length} employé{selectedItems.length > 1 ? 's' : ''} sélectionné{selectedItems.length > 1 ? 's' : ''}
                            </span>
                            <button
                                type="button"
                                onClick={() => {
                                    setSelectedItems([]);
                                    setSelectedAll(false);
                                }}
                                className="text-xs text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 underline"
                            >
                                Désélectionner
                            </button>
                        </div>
                        <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 lg:ml-auto">
                            <button type="button" onClick={() => handleBulkUpdate('active', true)} className={bulkButtonClass}>
                                <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0"/>
                                Activer
                            </button>
                            <button type="button" onClick={() => handleBulkUpdate('active', false)} className={bulkButtonClass}>
                                <XCircle className="w-4 h-4 text-gray-500 flex-shrink-0"/>
                                Désactiver
                            </button>
                            <button type="button" onClick={() => handleBulkUpdate('showOnCalendar', true)} className={bulkButtonClass}>
                                <CalendarCheck className="w-4 h-4 text-primary flex-shrink-0"/>
                                <span className="truncate">Afficher<span className="hidden sm:inline"> au calendrier</span></span>
                            </button>
                            <button type="button" onClick={() => handleBulkUpdate('showOnCalendar', false)} className={bulkButtonClass}>
                                <CalendarX className="w-4 h-4 text-amber-600 flex-shrink-0"/>
                                <span className="truncate">Masquer<span className="hidden sm:inline"> du calendrier</span></span>
                            </button>
                            <button type="button" onClick={handleBulkDelete}
                                    className={`${bulkButtonClass} !text-red-600 dark:!text-red-400 !border-red-200 dark:!border-red-800 hover:!bg-red-50 dark:hover:!bg-red-900/20 col-span-2 sm:col-span-1`}>
                                <Trash2 className="w-4 h-4 flex-shrink-0"/>
                                Supprimer
                            </button>
                        </div>
                    </div>
                )}

                {isLoading ? (
                    <ListLoader label="Chargement des employés..."/>
                ) : error ? (
                    <div className="p-4 text-center text-red-500">Erreur : {error.message}</div>
                ) : (
                <div className="bg-white dark:bg-gray-800 shadow-sm border border-gray-200 dark:border-gray-700">
                    {filteredEmployees.length === 0 ? (
                        <ListEmpty
                            icon={Users}
                            title={debouncedSearchTerm ? 'Aucun employé trouvé' : 'Aucun employé'}
                            text={debouncedSearchTerm ? 'Ajustez la recherche.' : 'Ajoutez les employés qui utilisent la caisse.'}
                        />
                    ) : (
                    <>
                    {/* Desktop table — rows can be dragged to reorder; no overflow clipping so the menus stay visible */}
                    <div className="hidden md:block">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-700/50">
                            <tr>
                                <th className="w-10 pl-6 pr-0 py-4"><span className="sr-only">Ordre</span></th>
                                <th className="w-12 px-6 py-4 text-left">
                                    <input
                                        type="checkbox"
                                        aria-label="Tout sélectionner"
                                        checked={selectedAll}
                                        onChange={handleSelectAll}
                                        className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-primary focus:ring-primary"
                                    />
                                </th>
                                <th className={thClass}>Nom</th>
                                <th className={thClass}>Email</th>
                                <th className={`${thClass} hidden lg:table-cell`}>Profil</th>
                                <th className={thClass}>Statut</th>
                                <th className={`${thClass} text-right`}>Actions</th>
                            </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {filteredEmployees.map((employee, index) => (
                                employee ? (
                                    <DraggableEmployeeRow
                                        key={employee.id}
                                        employee={employee}
                                        index={index}
                                        moveEmployee={moveEmployee}
                                        isSelected={selectedItems.includes(employee.id)}
                                        onSelectItem={handleSelectItem}
                                    >
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3 min-w-0">
                                                <EmployeeAvatar employee={employee} size={40} className="rounded-lg flex-shrink-0"/>
                                                <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                                                    {employee.name || 'Sans nom'}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">
                                            {employee.email || '-'}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300 hidden lg:table-cell">
                                            {employee.profil?.label || '-'}
                                        </td>
                                        <td className="px-6 py-4">
                                            <EmployeeBadges employee={employee}/>
                                        </td>
                                        <td
                                            className="px-6 py-4 text-right"
                                            data-no-drag="true"
                                            onClick={(e) => e.stopPropagation()}
                                            onMouseDown={(e) => e.stopPropagation()}
                                        >
                                            {rowMenu(employee)}
                                        </td>
                                    </DraggableEmployeeRow>
                                ) : <React.Fragment key={index}/>
                            ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile rows — edge to edge, same as the Stock / catalogue lists */}
                    <div className="md:hidden divide-y divide-gray-200 dark:divide-gray-700">
                        {filteredEmployees.map((employee, index) => (
                            employee ? (
                                <div
                                    key={employee.id}
                                    className={`${mobileRowClass} ${selectedItems.includes(employee.id) ? '!bg-primary/5 dark:!bg-primary/10' : ''}`}
                                >
                                    <div className="flex items-start gap-3">
                                        <input
                                            type="checkbox"
                                            checked={selectedItems.includes(employee.id)}
                                            onChange={(e) => handleSelectItem(employee.id, e)}
                                            className="w-4 h-4 mt-1 rounded border-gray-300 dark:border-gray-600 text-primary focus:ring-primary flex-shrink-0"
                                        />
                                        <EmployeeAvatar employee={employee} size={48} className="rounded-lg flex-shrink-0"/>
                                        <div className="flex-1 min-w-0">
                                            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate">
                                                {employee.name || 'Sans nom'}
                                            </h3>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                                {employee.email || 'Pas d\'email'}
                                            </p>
                                            {employee.profil?.label && (
                                                <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                                                    {employee.profil.label}
                                                </p>
                                            )}
                                            <div className="mt-2">
                                                <EmployeeBadges employee={employee}/>
                                            </div>
                                        </div>
                                        <div className="flex-shrink-0" data-no-drag="true">
                                            {rowMenu(employee)}
                                        </div>
                                    </div>
                                </div>
                            ) : <React.Fragment key={index}/>
                        ))}
                    </div>
                    </>
                    )}
                </div>
                )}

                {!isLoading && !error && (
                    <p className="py-4 px-4 text-sm text-gray-600 dark:text-gray-400">
                        {filteredEmployees.length} employé{filteredEmployees.length > 1 ? 's' : ''}
                        {debouncedSearchTerm && employees && ` sur ${employees.length} au total`}
                    </p>
                )}

                {/* Modal PIN */}
                {showPinModal && selectedEmployeePin && (
                    <Modal
                        title="Code PIN de l'employé"
                        subtitle={selectedEmployeePin.name}
                        onClose={closePinModal}
                        size="sm"
                        footer={(
                            <div className="border-t border-gray-200 dark:border-gray-700">
                                <button type="button" onClick={closePinModal}
                                        className="w-full py-4 font-medium text-primary hover:bg-gray-50 dark:hover:bg-gray-700">
                                    Fermer
                                </button>
                            </div>
                        )}
                    >
                        {fetchEmployeePinMutation.isPending ? (
                            <ListLoader label="Chargement du PIN..."/>
                        ) : (
                            <div className="space-y-4">
                                <div className="flex items-center justify-center bg-gray-50 dark:bg-gray-700/30 rounded-lg p-8 border border-gray-200 dark:border-gray-700">
                                    <p className="text-5xl font-bold text-primary tracking-[0.5em] tabular-nums">
                                        {selectedEmployeePin.password || '••••'}
                                    </p>
                                </div>
                                <p className="text-xs text-amber-800 dark:text-amber-400 text-center bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                                    <span className="font-semibold">Confidentiel</span> — ce code PIN sert à l'authentification de
                                    l'employé sur les terminaux de vente.
                                </p>
                            </div>
                        )}
                    </Modal>
                )}

                {/* Modal de Confirmation */}
                {showConfirmDialog && confirmAction && (
                    <Modal
                        title="Confirmation"
                        onClose={closeConfirm}
                        size="sm"
                        footer={(
                            <ModalFooter
                                onCancel={closeConfirm}
                                onConfirm={confirmDialogAction}
                                label={isDanger ? 'Supprimer' : 'Confirmer'}
                                tone={isDanger ? 'text-red-600 dark:text-red-400' : 'text-primary'}
                            />
                        )}
                    >
                        <p className="text-sm text-gray-600 dark:text-gray-300 text-center py-2">
                            {confirmAction.message}
                        </p>
                    </Modal>
                )}
            </div>
        </DndProvider>
    );
};

export default EmployeesList;
